import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  FileText, 
  Download, 
  Calendar, 
  Users,
  RefreshCw,
  FileSpreadsheet,
  Printer,
  TrendingUp,
  AlertTriangle,
  Activity,
  AppWindow,
  Clock,
  Shield,
  Eye,
  Briefcase,
  CheckCircle,
  XCircle,
  AlertCircle,
  MapPin,
  Camera,
  Coffee,
  BarChart2,
  Globe
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { formatTimeFromISO } from '@/utils/timezoneUtils';
import { useLanguage } from '@/i18n/LanguageContext';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

function ActivityReportsPage() {
  const { t, isRTL } = useLanguage();
  const [activeTab, setActiveTab] = useState('productivity');
  const [dateRange, setDateRange] = useState('monthly');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedStaff, setSelectedStaff] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [staffList, setStaffList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [timelineDate, setTimelineDate] = useState('');
  const [timelineStaff, setTimelineStaff] = useState('');
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false);
  const [timelineData, setTimelineData] = useState(null);
  const [avgStatsData, setAvgStatsData] = useState(null);

  useEffect(() => {
    setDateRangePreset('monthly');
    fetchStaffList();
  }, []);

  const setDateRangePreset = (preset) => {
    const today = new Date();
    let start, end;

    switch (preset) {
      case 'daily':
        start = end = today;
        break;
      case 'weekly':
        start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'monthly':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = today;
        break;
      case 'custom':
        return;
      default:
        start = end = today;
    }

    setFromDate(start.toISOString().split('T')[0]);
    setToDate(end.toISOString().split('T')[0]);
    setTimelineDate(today.toISOString().split('T')[0]);
    setDateRange(preset);
  };

  const fetchStaffList = async () => {
    try {
      const response = await axios.get(`${API}/admin/staff`, { withCredentials: true });
      setStaffList(response.data);
      const depts = [...new Set(response.data.map(s => s.department).filter(Boolean))];
      setDepartments(depts);
      if (response.data.length > 0) {
        setTimelineStaff(response.data[0].staff_id);
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    setReportData(null);
    setAvgStatsData(null);

    try {
      const params = {
        start_date: fromDate,
        end_date: toDate
      };

      if (selectedStaff !== 'all') params.staff_id = selectedStaff;
      if (selectedDepartment !== 'all') params.department = selectedDepartment;

      if (activeTab === 'avg-stats') {
        try {
          const response = await axios.get(`${API}/admin/activity-reports/avg-stats`, {
            params,
            withCredentials: true
          });
          setAvgStatsData(response.data);
        } catch (error) {
          console.error('Failed to generate avg stats report:', error);
        } finally {
          setLoading(false);
        }
        return;
      }

      const endpoints = {
        productivity: '/admin/activity-reports/productivity',
        'app-usage': '/admin/activity-reports/app-usage',
        compliance: '/admin/activity-reports/attendance-compliance',
        exceptions: '/admin/activity-reports/exceptions'
      };

      const response = await axios.get(`${API}${endpoints[activeTab]}`, {
        params,
        withCredentials: true
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Failed to generate report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewTimeline = async () => {
    if (!timelineStaff || !timelineDate) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/activity-reports/timeline`, {
        params: { date: timelineDate, staff_id: timelineStaff },
        withCredentials: true
      });
      setTimelineData(response.data);
      setTimelineDialogOpen(true);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = () => {
    if (!reportData?.records?.length) return;

    let csvContent = '';
    const records = reportData.records;
    const excludeFields = ['violations', 'top_apps'];
    const headers = Object.keys(records[0]).filter(k => !excludeFields.includes(k));
    csvContent = headers.join(',') + '\n';

    records.forEach(record => {
      const row = headers.map(h => {
        const val = record[h];
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : val ?? '';
      });
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${activeTab}-report-${fromDate}-to-${toDate}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleExportPDF = () => {
    const printWindow = window.open('', '_blank');
    const reportTitle = {
      productivity: 'Productivity Report',
      'app-usage': 'Application Usage Report',
      compliance: 'Attendance Compliance Report',
      exceptions: 'Work Quality Exceptions Report'
    }[activeTab];

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>${reportTitle}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; border-bottom: 2px solid #6366f1; padding-bottom: 10px; }
          .summary { background: #f3f4f6; padding: 15px; border-radius: 8px; margin: 20px 0; display: flex; flex-wrap: wrap; gap: 20px; }
          .summary-item { min-width: 150px; }
          .summary-label { font-size: 12px; color: #666; }
          .summary-value { font-size: 18px; font-weight: bold; color: #333; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background: #6366f1; color: white; padding: 12px 8px; text-align: left; font-size: 12px; }
          td { padding: 10px 8px; border-bottom: 1px solid #e5e7eb; font-size: 11px; }
          tr:nth-child(even) { background: #f9fafb; }
          .good { color: #16a34a; } .warning { color: #d97706; } .bad { color: #dc2626; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${reportTitle}</h1>
        <p>Period: ${fromDate} to ${toDate}</p>
        ${generatePDFContent()}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const generatePDFContent = () => {
    if (!reportData) return '';
    let html = '<div class="summary">';
    Object.entries(reportData.summary || {}).forEach(([key, value]) => {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      html += `<div class="summary-item"><div class="summary-label">${label}</div><div class="summary-value">${value}</div></div>`;
    });
    html += '</div>';

    if (reportData.records?.length) {
      const excludeFields = ['violations', 'top_apps', 'staff_id'];
      const headers = Object.keys(reportData.records[0]).filter(k => !excludeFields.includes(k));
      html += '<table><thead><tr>';
      headers.forEach(h => { html += `<th>${h.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</th>`; });
      html += '</tr></thead><tbody>';
      reportData.records.forEach(record => {
        html += '<tr>';
        headers.forEach(h => { html += `<td>${record[h] ?? '-'}</td>`; });
        html += '</tr>';
      });
      html += '</tbody></table>';
    }
    return html;
  };

  const formatHours = (hours) => hours ? `${hours.toFixed(1)}h` : '-';
  
  const getEfficiencyColor = (pct) => {
    if (pct >= 80) return 'text-indigo-600';
    if (pct >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getSeverityBadge = (severity) => {
    const styles = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-blue-100 text-blue-700 border-blue-200'
    };
    return <Badge className={styles[severity] || styles.low}>{severity?.toUpperCase()}</Badge>;
  };

  const getTimelineIcon = (type) => {
    const icons = {
      check_in: <CheckCircle className="w-4 h-4 text-indigo-500" />,
      check_out: <XCircle className="w-4 h-4 text-red-500" />,
      break_start: <Coffee className="w-4 h-4 text-orange-500" />,
      break_end: <Activity className="w-4 h-4 text-blue-500" />,
      idle: <AlertCircle className="w-4 h-4 text-amber-500" />,
      screenshot: <Camera className="w-4 h-4 text-purple-500" />,
      location: <MapPin className="w-4 h-4 text-cyan-500" />
    };
    return icons[type] || <Activity className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="space-y-6" data-testid="activity-reports-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Activity Reports</h2>
          <p className="text-gray-600 mt-1">Monitor productivity, app usage, and work quality</p>
        </div>
      </div>

      {/* Filters Card */}
      <Card className="border-2 border-purple-100 shadow-md" data-testid="report-filters">
        <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <Activity className="w-5 h-5" />
            Report Filters
          </CardTitle>
          <CardDescription>Select date range and filters to generate activity reports</CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                Date Range
              </Label>
              <Select value={dateRange} onValueChange={setDateRangePreset}>
                <SelectTrigger data-testid="date-range-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Today</SelectItem>
                  <SelectItem value="weekly">This Week</SelectItem>
                  <SelectItem value="monthly">This Month</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">From Date</Label>
              <Input type="date" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setDateRange('custom'); }} />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">To Date</Label>
              <Input type="date" value={toDate} onChange={(e) => { setToDate(e.target.value); setDateRange('custom'); }} />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Briefcase className="w-4 h-4 text-gray-500" />
                Department
              </Label>
              <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
                <SelectTrigger>
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-gray-500" />
                Staff
              </Label>
              <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                <SelectTrigger>
                  <SelectValue placeholder="All Staff" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  {staffList.map((staff) => (
                    <SelectItem key={staff.staff_id} value={staff.staff_id}>{staff.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>&nbsp;</Label>
              <Button onClick={handleGenerateReport} className="w-full bg-purple-600 hover:bg-purple-700" disabled={loading} data-testid="generate-report-btn">
                {loading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><FileText className="w-4 h-4 mr-2" />Generate</>}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setReportData(null); setAvgStatsData(null); }} className="w-full">
        <TabsList className="grid grid-cols-6 w-full bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="productivity" className="rounded-lg data-[state=active]:bg-white" data-testid="productivity-tab">
            <TrendingUp className="w-4 h-4 mr-2" />
            Productivity
          </TabsTrigger>
          <TabsTrigger value="app-usage" className="rounded-lg data-[state=active]:bg-white" data-testid="app-usage-tab">
            <AppWindow className="w-4 h-4 mr-2" />
            App Usage
          </TabsTrigger>
          <TabsTrigger value="compliance" className="rounded-lg data-[state=active]:bg-white" data-testid="compliance-tab">
            <Shield className="w-4 h-4 mr-2" />
            Compliance
          </TabsTrigger>
          <TabsTrigger value="timeline" className="rounded-lg data-[state=active]:bg-white" data-testid="timeline-tab">
            <Clock className="w-4 h-4 mr-2" />
            Timeline
          </TabsTrigger>
          <TabsTrigger value="exceptions" className="rounded-lg data-[state=active]:bg-white" data-testid="exceptions-tab">
            <AlertTriangle className="w-4 h-4 mr-2" />
            Exceptions
          </TabsTrigger>
          <TabsTrigger value="avg-stats" className="rounded-lg data-[state=active]:bg-white" data-testid="avg-stats-tab">
            <BarChart2 className="w-4 h-4 mr-2" />
            Avg Stats
          </TabsTrigger>
        </TabsList>

        {/* Productivity Report */}
        <TabsContent value="productivity" className="mt-6">
          {reportData && activeTab === 'productivity' ? (
            <Card className="shadow-lg" data-testid="productivity-report">
              <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-indigo-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-indigo-800">
                      <TrendingUp className="w-5 h-5" />
                      Productivity Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_staff} staff analyzed</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" /> CSV</Button>
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-5 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">{formatHours(reportData.summary?.total_active_hours)}</p>
                    <p className="text-sm text-gray-500">Active Time</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{formatHours(reportData.summary?.total_idle_hours)}</p>
                    <p className="text-sm text-gray-500">Idle Time</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{formatHours(reportData.summary?.total_break_hours)}</p>
                    <p className="text-sm text-gray-500">Break Time</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${getEfficiencyColor(reportData.summary?.overall_efficiency)}`}>
                      {reportData.summary?.overall_efficiency}%
                    </p>
                    <p className="text-sm text-gray-500">Overall Efficiency</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">{reportData.summary?.total_staff}</p>
                    <p className="text-sm text-gray-500">{t("dashboard.total_staff")}</p>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Staff</TableHead>
                      <TableHead>{t("staff.department")}</TableHead>
                      <TableHead className="text-center">Days</TableHead>
                      <TableHead className="text-center">{t("common.active")}</TableHead>
                      <TableHead className="text-center">Idle</TableHead>
                      <TableHead className="text-center">Break</TableHead>
                      <TableHead className="text-center">Efficiency</TableHead>
                      <TableHead className="text-center">Avg/Day</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.records?.map((record, i) => (
                      <TableRow key={i} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                        <TableCell><Badge variant="outline">{record.department}</Badge></TableCell>
                        <TableCell className="text-center">{record.days_worked}</TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-mono">{formatHours(record.active_hours)}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded font-mono">{formatHours(record.idle_hours)}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded font-mono">{formatHours(record.break_hours)}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center gap-2">
                            <Progress value={record.efficiency_percent} className="w-16 h-2" />
                            <span className={`font-semibold ${getEfficiencyColor(record.efficiency_percent)}`}>{record.efficiency_percent}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-medium">{formatHours(record.avg_daily_hours)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <TrendingUp className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Productivity Report</h3>
                <p className="text-gray-500 mt-2">Active time vs idle time, efficiency metrics per staff</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* App Usage Report */}
        <TabsContent value="app-usage" className="mt-6">
          {reportData && activeTab === 'app-usage' ? (
            <Card className="shadow-lg" data-testid="app-usage-report">
              <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-cyan-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-blue-800">
                      <AppWindow className="w-5 h-5" />
                      Application & Website Usage Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_staff} staff with app usage data</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" /> CSV</Button>
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">{formatHours(reportData.summary?.total_productive_hours)}</p>
                    <p className="text-sm text-gray-500">Productive</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{formatHours(reportData.summary?.total_unproductive_hours)}</p>
                    <p className="text-sm text-gray-500">Unproductive</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{reportData.summary?.total_blocked_attempts}</p>
                    <p className="text-sm text-gray-500">Blocked Attempts</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">{reportData.summary?.total_staff}</p>
                    <p className="text-sm text-gray-500">{t("dashboard.total_staff")}</p>
                  </div>
                </div>

                {reportData.top_apps?.length > 0 && (
                  <div className="p-4 border-b">
                    <h4 className="font-semibold mb-3">Top Apps Across Company</h4>
                    <div className="flex flex-wrap gap-2">
                      {reportData.top_apps.map((app, i) => (
                        <div key={i} className={`rounded-lg px-3 py-2 text-sm ${
                          app.category === 'productive' ? 'bg-indigo-50 border border-indigo-200' :
                          app.category === 'unproductive' ? 'bg-red-50 border border-red-200' :
                          'bg-gray-50 border border-gray-200'
                        }`}>
                          <span className="font-medium">{app.app_name}</span>
                          <span className="text-gray-500 ml-2">{formatHours(Math.round(app.total_duration_seconds / 3600 * 100) / 100)}</span>
                          {app.staff_count > 0 && <span className="text-gray-400 ml-1 text-xs">({app.staff_count} staff)</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Staff</TableHead>
                      <TableHead>{t("staff.department")}</TableHead>
                      <TableHead className="text-center">Productive</TableHead>
                      <TableHead className="text-center">Unproductive</TableHead>
                      <TableHead className="text-center">Neutral</TableHead>
                      <TableHead className="text-center">Blocked</TableHead>
                      <TableHead className="text-center">Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(reportData.staff_usage || reportData.records)?.map((record, i) => (
                      <TableRow key={i} className="hover:bg-gray-50">
                        <TableCell className="font-medium">{record.staff_name}</TableCell>
                        <TableCell><Badge variant="outline">{record.department}</Badge></TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">{formatHours(record.productive_hours)}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded">{formatHours(record.unproductive_hours)}</span>
                        </TableCell>
                        <TableCell className="text-center">{formatHours(record.neutral_hours)}</TableCell>
                        <TableCell className="text-center">
                          {record.blocked_attempts > 0 ? (
                            <Badge variant="destructive">{record.blocked_attempts}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-semibold ${getEfficiencyColor(record.productivity_score)}`}>
                            {record.productivity_score}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <AppWindow className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Application Usage Report</h3>
                <p className="text-gray-500 mt-2">Productive vs unproductive app time, blocked attempts</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Compliance Report */}
        <TabsContent value="compliance" className="mt-6">
          {reportData && activeTab === 'compliance' ? (
            <Card className="shadow-lg" data-testid="compliance-report">
              <CardHeader className="border-b bg-gradient-to-r from-indigo-50 to-violet-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-indigo-800">
                      <Shield className="w-5 h-5" />
                      Attendance Compliance Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_staff} staff members</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportCSV}><Download className="w-4 h-4 mr-2" /> CSV</Button>
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-5 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-indigo-600">{reportData.summary?.total_on_time}</p>
                    <p className="text-sm text-gray-500">On Time</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{reportData.summary?.total_late_arrivals}</p>
                    <p className="text-sm text-gray-500">Late Arrivals</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{reportData.summary?.total_early_leaves}</p>
                    <p className="text-sm text-gray-500">Early Leaves</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{reportData.summary?.total_missed_checkins}</p>
                    <p className="text-sm text-gray-500">Missed Check-ins</p>
                  </div>
                  <div className="text-center">
                    <p className={`text-2xl font-bold ${getEfficiencyColor(reportData.summary?.overall_compliance)}`}>
                      {reportData.summary?.overall_compliance}%
                    </p>
                    <p className="text-sm text-gray-500">Compliance</p>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Staff</TableHead>
                      <TableHead>Shift</TableHead>
                      <TableHead className="text-center">Expected</TableHead>
                      <TableHead className="text-center">Present</TableHead>
                      <TableHead className="text-center">On Time</TableHead>
                      <TableHead className="text-center">Late</TableHead>
                      <TableHead className="text-center">Early Leave</TableHead>
                      <TableHead className="text-center">Compliance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportData.records?.map((record, i) => (
                      <TableRow key={i} className="hover:bg-gray-50">
                        <TableCell>
                          <div>
                            <p className="font-medium">{record.staff_name}</p>
                            <p className="text-xs text-gray-500">{record.department}</p>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">{record.shift}</TableCell>
                        <TableCell className="text-center">{record.expected_days}</TableCell>
                        <TableCell className="text-center">{record.present_days}</TableCell>
                        <TableCell className="text-center">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded">{record.on_time_days}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          {record.late_arrivals > 0 ? (
                            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded">{record.late_arrivals}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          {record.early_leaves > 0 ? (
                            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">{record.early_leaves}</span>
                          ) : '-'}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`font-semibold ${getEfficiencyColor(record.compliance_rate)}`}>
                            {record.compliance_rate}%
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Attendance Compliance Report</h3>
                <p className="text-gray-500 mt-2">Late arrivals, early leaves, missed check-ins, shift compliance</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-6">
          <Card className="shadow-lg" data-testid="timeline-section">
            <CardHeader className="border-b bg-gradient-to-r from-cyan-50 to-sky-50">
              <CardTitle className="flex items-center gap-2 text-cyan-800">
                <Clock className="w-5 h-5" />
                Activity Timeline
              </CardTitle>
              <CardDescription>View minute-by-minute activity for a specific staff member</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Select Staff</Label>
                  <Select value={timelineStaff} onValueChange={setTimelineStaff}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {staffList.map((staff) => (
                        <SelectItem key={staff.staff_id} value={staff.staff_id}>{staff.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Select Date</Label>
                  <Input type="date" value={timelineDate} onChange={(e) => setTimelineDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>&nbsp;</Label>
                  <Button onClick={handleViewTimeline} className="w-full bg-cyan-600 hover:bg-cyan-700" disabled={loading || !timelineStaff}>
                    <Eye className="w-4 h-4 mr-2" /> View Timeline
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exceptions Report */}
        <TabsContent value="exceptions" className="mt-6">          {reportData && activeTab === 'exceptions' ? (
            <Card className="shadow-lg" data-testid="exceptions-report">
              <CardHeader className="border-b bg-gradient-to-r from-red-50 to-orange-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-red-800">
                      <AlertTriangle className="w-5 h-5" />
                      Work Quality Exceptions Report
                    </CardTitle>
                    <CardDescription>{reportData.summary?.total_exceptions} exceptions found</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleExportPDF}><Printer className="w-4 h-4 mr-2" /> PDF</Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 border-b">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{reportData.summary?.high_severity}</p>
                    <p className="text-sm text-gray-500">High Severity</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-amber-600">{reportData.summary?.medium_severity}</p>
                    <p className="text-sm text-gray-500">Medium Severity</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{reportData.summary?.low_severity}</p>
                    <p className="text-sm text-gray-500">Low Severity</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-gray-600">{reportData.summary?.staff_with_exceptions}</p>
                    <p className="text-sm text-gray-500">Staff Affected</p>
                  </div>
                </div>

                {reportData.by_staff?.length > 0 && (
                  <div className="p-4 border-b">
                    <h4 className="font-semibold mb-3">By Staff</h4>
                    <div className="flex flex-wrap gap-3">
                      {reportData.by_staff.slice(0, 10).map((staff, i) => (
                        <div key={i} className="bg-red-50 rounded-lg p-3 border border-red-200">
                          <p className="font-medium">{staff.staff_name}</p>
                          <p className="text-sm text-gray-600">{staff.total_exceptions} exceptions</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {reportData.exceptions?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>{t("common.date")}</TableHead>
                        <TableHead>Staff</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Detail</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.exceptions.map((exc, i) => (
                        <TableRow key={i} className="hover:bg-gray-50">
                          <TableCell>{exc.date}</TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium">{exc.staff_name}</p>
                              <p className="text-xs text-gray-500">{exc.department}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{exc.type?.replace(/_/g, ' ')}</Badge>
                          </TableCell>
                          <TableCell>{getSeverityBadge(exc.severity)}</TableCell>
                          <TableCell className="text-gray-600">{exc.detail}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="p-8 text-center text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto text-indigo-400 mb-4" />
                    <p>No exceptions found in this period</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <AlertTriangle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Work Quality Exceptions Report</h3>
                <p className="text-gray-500 mt-2">Long idle time, suspicious patterns, work quality issues</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Avg Stats Report */}
        <TabsContent value="avg-stats" className="mt-6">
          {avgStatsData ? (
            <Card className="shadow-lg" data-testid="avg-stats-report">
              <CardHeader className="border-b bg-gradient-to-r from-violet-50 to-purple-50">
                <div className="flex justify-between items-center">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-violet-800">
                      <BarChart2 className="w-5 h-5" />
                      Avg Stats Report
                      <Badge variant="outline" className="ml-2 text-violet-600 border-violet-300">
                        {avgStatsData.mode === 'date_wise' ? `${avgStatsData.staff?.name}` : 'All Staff'}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      {avgStatsData.mode === 'date_wise'
                        ? `Date-wise breakdown · ${avgStatsData.summary?.total_days} day(s)`
                        : `Staff-wise averages · ${avgStatsData.summary?.total_staff} staff member(s)`}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="pt-6 space-y-6">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="rounded-xl bg-violet-50 border border-violet-200 p-4 text-center">
                    <p className="text-2xl font-bold text-violet-700">{avgStatsData.summary?.avg_working_hrs}h</p>
                    <p className="text-sm text-gray-500 mt-1">Avg Working Hrs</p>
                  </div>
                  <div className="rounded-xl bg-orange-50 border border-orange-200 p-4 text-center">
                    <p className="text-2xl font-bold text-orange-600">{avgStatsData.summary?.avg_break_hrs}h</p>
                    <p className="text-sm text-gray-500 mt-1">Avg Break Hrs</p>
                  </div>
                  <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-center">
                    <p className="text-2xl font-bold text-amber-600">{avgStatsData.summary?.avg_idle_hrs}h</p>
                    <p className="text-sm text-gray-500 mt-1">Avg Idle Hrs</p>
                  </div>
                  <div className="rounded-xl bg-indigo-50 border border-indigo-200 p-4 text-center">
                    <p className="text-2xl font-bold text-indigo-600">{avgStatsData.summary?.avg_productive_hrs}h</p>
                    <p className="text-sm text-gray-500 mt-1">Avg Productive Hrs</p>
                  </div>
                </div>

                {/* Bar Chart */}
                {avgStatsData.records?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                      <BarChart2 className="w-4 h-4 text-violet-500" />
                      {avgStatsData.mode === 'date_wise' ? 'Daily Breakdown' : 'Staff Comparison'}
                    </h4>
                    <ResponsiveContainer width="100%" height={320}>
                      <BarChart
                        data={avgStatsData.records.map(r => ({
                          name: avgStatsData.mode === 'date_wise' ? r.date : r.staff_name,
                          'Productive': r.productive_hrs ?? r.avg_productive_hrs,
                          'Break': r.break_hrs ?? r.avg_break_hrs,
                          'Idle': r.idle_hrs ?? r.avg_idle_hrs,
                        }))}
                        margin={{ top: 5, right: 20, left: 0, bottom: 60 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="name"
                          tick={{ fontSize: 11 }}
                          angle={avgStatsData.mode === 'staff_wise' && avgStatsData.records.length > 5 ? -35 : 0}
                          textAnchor={avgStatsData.mode === 'staff_wise' && avgStatsData.records.length > 5 ? 'end' : 'middle'}
                          interval={0}
                        />
                        <YAxis tick={{ fontSize: 11 }} unit="h" />
                        <Tooltip formatter={(val) => `${val}h`} />
                        <Legend />
                        <Bar dataKey="Productive" fill="#16a34a" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Break" fill="#f97316" radius={[4, 4, 0, 0]} />
                        <Bar dataKey="Idle" fill="#d97706" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Data Table */}
                <div>
                  <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-violet-500" />
                    Detailed Data
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        {avgStatsData.mode === 'date_wise' ? (
                          <>
                            <TableHead>Date</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-center">Working Hrs</TableHead>
                            <TableHead className="text-center">Break Hrs</TableHead>
                            <TableHead className="text-center">Idle Hrs</TableHead>
                            <TableHead className="text-center">Productive Hrs</TableHead>
                          </>
                        ) : (
                          <>
                            <TableHead>Staff</TableHead>
                            <TableHead>Department</TableHead>
                            <TableHead className="text-center">Days</TableHead>
                            <TableHead className="text-center">Avg Working</TableHead>
                            <TableHead className="text-center">Avg Break</TableHead>
                            <TableHead className="text-center">Avg Idle</TableHead>
                            <TableHead className="text-center">Avg Productive</TableHead>
                          </>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {avgStatsData.records?.map((r, i) => (
                        avgStatsData.mode === 'date_wise' ? (
                          <TableRow key={i} className="hover:bg-gray-50">
                            <TableCell className="font-medium">{r.date}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={
                                r.status === 'present' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' :
                                r.status === 'late' ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                'bg-gray-50 text-gray-600 border-gray-200'
                              }>{r.status}</Badge>
                            </TableCell>
                            <TableCell className="text-center font-mono">{r.working_hrs}h</TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded font-mono text-sm">{r.break_hrs}h</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded font-mono text-sm">{r.idle_hrs}h</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-mono text-sm font-semibold">{r.productive_hrs}h</span>
                            </TableCell>
                          </TableRow>
                        ) : (
                          <TableRow key={i} className="hover:bg-gray-50">
                            <TableCell className="font-medium">{r.staff_name}</TableCell>
                            <TableCell><Badge variant="outline">{r.department}</Badge></TableCell>
                            <TableCell className="text-center">{r.days_worked}</TableCell>
                            <TableCell className="text-center font-mono">{r.avg_working_hrs}h</TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded font-mono text-sm">{r.avg_break_hrs}h</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded font-mono text-sm">{r.avg_idle_hrs}h</span>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="px-2 py-1 bg-indigo-100 text-indigo-700 rounded font-mono text-sm font-semibold">{r.avg_productive_hrs}h</span>
                            </TableCell>
                          </TableRow>
                        )
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border-dashed border-2 border-gray-300">
              <CardContent className="py-16 text-center">
                <BarChart2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900">Avg Stats Report</h3>
                <p className="text-gray-500 mt-2">Average Break, Idle &amp; Productive hours per staff or per day</p>
                <p className="text-sm text-gray-400 mt-2">Select "All Staff" for staff-wise view, or pick a staff for date-wise breakdown</p>
                <p className="text-sm text-gray-400 mt-4">Select date range and click Generate</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Report Type Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-4">
            <TrendingUp className="w-6 h-6 text-indigo-500 mb-2" />
            <h4 className="font-semibold text-sm">Productivity</h4>
            <p className="text-xs text-gray-500">Active vs idle time</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <AppWindow className="w-6 h-6 text-blue-500 mb-2" />
            <h4 className="font-semibold text-sm">App Usage</h4>
            <p className="text-xs text-gray-500">Productive apps</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-indigo-500">
          <CardContent className="pt-4">
            <Shield className="w-6 h-6 text-indigo-500 mb-2" />
            <h4 className="font-semibold text-sm">Compliance</h4>
            <p className="text-xs text-gray-500">Shift adherence</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-cyan-500">
          <CardContent className="pt-4">
            <Clock className="w-6 h-6 text-cyan-500 mb-2" />
            <h4 className="font-semibold text-sm">Timeline</h4>
            <p className="text-xs text-gray-500">Activity log</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-red-500">
          <CardContent className="pt-4">
            <AlertTriangle className="w-6 h-6 text-red-500 mb-2" />
            <h4 className="font-semibold text-sm">Exceptions</h4>
            <p className="text-xs text-gray-500">Quality issues</p>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="pt-4">
            <BarChart2 className="w-6 h-6 text-violet-500 mb-2" />
            <h4 className="font-semibold text-sm">Avg Stats</h4>
            <p className="text-xs text-gray-500">Break/Idle/Productive</p>
          </CardContent>
        </Card>
      </div>

      {/* Timeline Dialog */}
      <Dialog open={timelineDialogOpen} onOpenChange={setTimelineDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Activity Timeline - {timelineData?.staff?.name}
            </DialogTitle>
          </DialogHeader>
          
          {timelineData && (
            <div className="space-y-4">
              <div className="grid grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-center">
                  <p className="text-lg font-bold">{timelineData.summary?.total_events}</p>
                  <p className="text-xs text-gray-500">Total Events</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold">{timelineData.summary?.screenshots_count}</p>
                  <p className="text-xs text-gray-500">Screenshots</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold">{timelineData.summary?.gps_updates}</p>
                  <p className="text-xs text-gray-500">GPS Updates</p>
                </div>
                <div className="text-center">
                  <p className="text-lg font-bold">{timelineData.summary?.idle_reports}</p>
                  <p className="text-xs text-gray-500">Idle Reports</p>
                </div>
              </div>

              <div className="space-y-2">
                {timelineData.timeline?.map((event, i) => (
                  <div key={i} className={`flex items-start gap-3 p-3 bg-white border rounded-lg hover:bg-gray-50 ${
                    event.type === 'app_usage' && event.domain ? 'border-l-4 border-l-blue-200' : ''
                  }`}>
                    <div className="mt-1">{getTimelineIcon(event.type)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start gap-2">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{event.description}</p>
                          {/* Show URL for browser app events */}
                          {event.type === 'app_usage' && event.domain && (
                            <p className="text-sm text-blue-600 truncate flex items-center gap-1 mt-0.5">
                              <Globe className="w-3 h-3 flex-shrink-0" />
                              {event.domain}
                            </p>
                          )}
                          {/* Show duration if available */}
                          {event.type === 'app_usage' && event.duration_seconds > 0 && (
                            <p className="text-xs text-gray-400 mt-0.5">
                              {Math.floor(event.duration_seconds / 60)}m {event.duration_seconds % 60}s
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {event.time ? formatTimeFromISO(event.time) : '-'}
                        </span>
                      </div>
                      {event.reason && <p className="text-sm text-gray-600">{event.reason}</p>}
                      {event.type === 'screenshot' && event.thumbnail_url && (
                        <img src={event.thumbnail_url} alt="Screenshot" className="mt-2 w-32 h-20 object-cover rounded border" />
                      )}
                      {event.type === 'location' && event.address && (
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {event.address}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
                {timelineData.timeline?.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No activity recorded for this date
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ActivityReportsPage;
